import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from '~/shadcn/components/ui/Button';

/**
 * Generic header for entity detail pages that renders a back navigation link,
 * entity identification content, and optional action buttons.
 *
 * @param {Object} props
 * @param {string|import('react-router-dom').To} props.backHref - Destination for the back navigation link.
 * @param {string} [props.backLabel='Back'] - Visible text for the back navigation link.
 * @param {Object} [props.backLinkProps] - Extra props forwarded to the `Link` component.
 * @param {React.ReactNode} [props.entityIdentificationComponent] - Custom content describing the entity (e.g. PO summary).
 * @param {string} [props.entityIdLabel] - Fallback label displayed when a custom component is not provided.
 * @returns {React.ReactElement}
 */
export function ViewEntityHeader({ backHref, backLabel, backLinkProps, entityIdentificationComponent, entityIdLabel }) {
    if (!backHref) {
        throw new Error('ViewEntityHeader requires a `backHref` prop.');
    }

    if (!entityIdentificationComponent && !entityIdLabel) {
        throw new Error('ViewEntityHeader requires either `entityIdentificationComponent` or `entityIdLabel`.');
    }

    return (
        <header className="flex flex-col gap-4 border-b border-border pb-4 lg:gap-6 lg:pb-6">
            <div className="flex w-full flex-wrap items-center gap-3">
                <div className="flex w-full items-center gap-3">
                    <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="h-auto shrink-0 px-0 py-0 text-muted-foreground hover:text-foreground"
                    >
                        <Link to={backHref} {...backLinkProps} className="flex items-center gap-2">
                            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                            <span className="text-sm font-medium">{backLabel}</span>
                        </Link>
                    </Button>

                    {entityIdentificationComponent ?? (
                        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{entityIdLabel}</h1>
                    )}
                </div>
            </div>
        </header>
    );
}
ViewEntityHeader.propTypes = {
    backHref: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    backLabel: PropTypes.string,
    backLinkProps: PropTypes.object,
    entityIdentificationComponent: PropTypes.node,
    entityIdLabel: PropTypes.string,
};

ViewEntityHeader.defaultProps = {
    backLabel: 'Back',
    backLinkProps: undefined,
    entityIdentificationComponent: undefined,
    entityIdLabel: undefined,
};

ViewEntityHeader.displayName = 'ViewEntityHeader';
